import Clarity from "@microsoft/clarity";
import { memo, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LexIcon } from "../../../../assets/icons/lex";
import { getLegislationById } from "../../../services";
import { getQueryValue } from "../../../store/selectors";
import {
  getHighlightedExcerpts,
  handleClickNote,
  handleInternalSpaNavigation,
  parseChapter,
  printLegislationContent,
  processDocumentText,
} from "../../../utils";
import { saveDocumentWithNotify } from "../../../utils/_handleSaveDocument";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal from "../../components/global/_notesModal";
import LegislationDetailsHeaderActions from "./components/LegislationDetailsHeaderActions";
import "./index.scss";

const HtmlContent = memo(({ html }) => (
  <div className="content-text" dangerouslySetInnerHTML={{ __html: html }} />
));

const LegislationDetails = (props) => {
  const { id } = useParams();

  const [legislation, setLegislation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideRevoked, setHideRevoked] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [modalType, setModalType] = useState("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const query = useSelector(getQueryValue);

  const { hash } = useLocation();

  const handlePrint = () => {
    printLegislationContent({
      legislation,
      hideRevoked,
    });
  };

  const handleOpenFindBar = () => {
    window.dispatchEvent(new CustomEvent("pdl:openFindBar"));
  };

  const stripAfterHtml = (value) => {
    if (!value) return value;

    const index = value.search(/<p[\s>]/i);

    if (index === -1) return value.trim();

    return value.slice(0, index).trim();
  };

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: legislation?._id ?? "",
      id: legislation?.id,
      tipo: "Legislacao",
      url: window.location.href,
    });

  const processedText = useMemo(() => {
    if (!legislation?.texto) return "";

    const nicknames =
      legislation?.apelidos?.filter((name) => name !== legislation.nome) || [];

    return processDocumentText({
      htmlText: `${
        nicknames ? `<p class="nicknames"> ${nicknames.join(" - ")} </p>` : ""
      } ${legislation.texto}`,
      hideRevoked,
    });
  }, [
    legislation?.texto,
    legislation?.apelidos,
    legislation?.nome,
    hideRevoked,
  ]);

  const mainHtml = useMemo(() => {
    if (!processedText) return "";
    return query
      ? getHighlightedExcerpts(
          processedText,
          query,
          processedText.length,
          false,
        )
      : processedText;
  }, [processedText, query]);

  const processedArticles = useMemo(() => {
    if (!legislation?.artigos?.length) return [];

    return legislation.artigos.map((art, index) => {
      const processedText = processDocumentText({
        htmlText: art.texto,
        hideRevoked,
      });

      const processedLivro = art.livro
        ? processDocumentText({ htmlText: art.livro, hideRevoked })
        : null;

      const processedTitulo = art.titulo
        ? processDocumentText({ htmlText: art.titulo, hideRevoked })
        : null;

      const processedCapitulo = art.capitulo
        ? processDocumentText({ htmlText: art.capitulo, hideRevoked })
        : null;

      const processedSecao = art.secao
        ? processDocumentText({ htmlText: art.secao, hideRevoked })
        : null;

      const highlighted = query
        ? getHighlightedExcerpts(
            processedText,
            query,
            processedText.length,
            false,
          )
        : processedText;

      return {
        ...art,
        _index: index,
        _highlighted: highlighted,

        livro: processedLivro,
        titulo: processedTitulo,
        capitulo: processedCapitulo,
        secao: processedSecao,
      };
    });
  }, [legislation?.artigos, hideRevoked, query]);

  useEffect(() => {
    const loadLegislation = async () => {
      setShowNoteModal(false);
      try {
        setLoading(true);
        setError(null);

        const response = await getLegislationById(id);

        setLegislation(response);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      } catch (err) {
        const status = err?.response?.status;

        setError(
          status === 404
            ? "Estamos trabalhando para disponibilizá-lo em breve."
            : err?.response?.data?.message ||
                "Não foi possível encontrar a legislação",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadLegislation();
    }
  }, [id]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const rawTarget = event.target;

      const target =
        rawTarget.closest?.("[data-nota-link], .nota-super, a") || rawTarget;
      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.hasAttribute("data-nota-link")) {
        event.preventDefault();

        const noteKey = target.getAttribute("data-nota-link");
        if (!noteKey) return;

        const note = legislation?.notas?.[noteKey];
        if (!note) return;

        let titleClicked = "";

        const tag = target.tagName.toLowerCase();

        if (
          tag === "a" ||
          tag === "strong" ||
          tag === "p" ||
          /^h[1-6]$/.test(tag)
        ) {
          titleClicked = target.textContent?.trim();
        }

        if (!titleClicked) {
          titleClicked = note?.descricao || `Nota ${noteKey}`;
        }

        const texto = note?.["texto:"] || note?.texto || "";
        setModalType("note");
        setNoteTitle(titleClicked);
        setNoteContent(processDocumentText({ htmlText: texto, hideRevoked }));
        setShowNoteModal(true);
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const url = new URL(href, window.location.origin);

        const artIdFromQuery = url.searchParams.get("artId");

        const match = href.match(/(art\d+)/i);

        const articlePart = artIdFromQuery || match?.[1];

        if (articlePart) {
          event.preventDefault();

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];
          cleaned = cleaned.split("?")[0];

          if (!artIdFromQuery) {
            if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
              cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
            }
          } else {
            cleaned = articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event, navigate)) {
            return;
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [legislation, showNoteModal, setNoteContent, hideRevoked]);

  useEffect(() => {
    if (loading) return;
    if (!legislation) return;

    if (!hash) return;

    const id = hash.replace("#", "");

    const timer = setTimeout(() => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        target.classList.add("highlight-scroll");
      } else {
        let retries = 0;
        const retryInterval = setInterval(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            clearInterval(retryInterval);
          }
          retries++;
          if (retries > 20) {
            clearInterval(retryInterval);
          }
        }, 200);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, legislation, hash]);

  useEffect(() => {
    if (!legislation) return;

    Clarity.event("legislation_view");

    Clarity.setTag("doc_type", "legislacao");
    Clarity.setTag("doc_id", legislation?.id);
    Clarity.setTag("doc_name", legislation?.nome);
  }, [legislation]);

  if (loading) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando legislação..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <LexIcon />
                <PDLTypography size="xl" variant="primary" weight="medium">
                  Este documento ainda não está disponível.
                </PDLTypography>
                <p className="text-muted">{error}</p>
                <p className="text-muted">
                  Se for urgente, fale com a gente. Teremos prazer em ajudar.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props} showHeader headerClass="details-sticky" key={hash}>
      <Container>
        <Row>
          <Col>
            <LegislationDetailsHeaderActions
              title={legislation?.nome || "Legislação"}
              hideRevoked={hideRevoked}
              isSavingDocument={isSavingDocument}
              onToggleHideRevoked={() => setHideRevoked((prev) => !prev)}
              onOpenFindBar={handleOpenFindBar}
              onSaveDocument={handleSaveDocument}
              onPrint={handlePrint}
            />
            <div id="details-content" className="mt-4">
              <div className="legislation content-box">
                {legislation?.texto ? (
                  <HtmlContent html={mainHtml} />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>

              <div className="content-box">
                {processedArticles.length > 0 &&
                  processedArticles.map((art) => {
                    const prev = processedArticles[art._index - 1];

                    const showBook =
                      !prev ||
                      stripAfterHtml(prev.livro) !== stripAfterHtml(art.livro);

                    const showTitle =
                      !prev ||
                      stripAfterHtml(prev.titulo) !==
                        stripAfterHtml(art.titulo);

                    const showChapter =
                      !prev ||
                      stripAfterHtml(prev.capitulo) !==
                        stripAfterHtml(art.capitulo);

                    const showSecao =
                      !prev ||
                      stripAfterHtml(prev.secao) !== stripAfterHtml(art.secao);

                    return (
                      <div key={art._index} className="article-item">
                        {showBook && art.livro && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.livro.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showTitle && art.titulo && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.titulo.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showChapter &&
                          art.capitulo &&
                          (() => {
                            const { title, notes } = parseChapter(
                              art.capitulo,
                              query,
                            );

                            return (
                              <div className="article-title-box">
                                <p
                                  className="article-chapter"
                                  dangerouslySetInnerHTML={{
                                    __html: String(title)?.replace(
                                      " - ",
                                      "<br/>",
                                    ),
                                  }}
                                />
                                {notes && (
                                  <div
                                    className="content-text"
                                    dangerouslySetInnerHTML={{ __html: notes }}
                                  />
                                )}
                              </div>
                            );
                          })()}

                        {showSecao && art.secao && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.secao.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        <HtmlContent html={art._highlighted} />
                      </div>
                    );
                  })}
              </div>
            </div>

            <NotesModal
              noteContent={noteContent}
              noteTitle={noteTitle}
              setShowNoteModal={setShowNoteModal}
              showNoteModal={showNoteModal}
              type={modalType}
              articleId={articleId}
              url={articleUrl}
            />
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default LegislationDetails;
